import React, { useEffect, useRef, useState } from 'react';

/**
 * Truly lazy video:
 * - Poster shows immediately (no network cost for video)
 * - Video <source> is only rendered when the element enters the viewport
 * - preload="metadata" keeps bandwidth low until user interacts
 * - Desktop: hover-to-play. Touch devices: tap-to-play (toggle).
 */
const LazyVideo = React.memo(({
  src,
  poster,
  className = "",
  controls = false,
  muted = true,
  loop = true,
  playsInline = true,
  autoPlay = false,
  hoverToPlay = false,
  aspectRatio = "16/9",
}) => {
  const wrapperRef = useRef(null);
  const videoRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [canHover, setCanHover] = useState(true);

  // Detect pointer capability (mobile = no hover)
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
    setCanHover(mq.matches);
    const handler = (e) => setCanHover(e.matches);
    mq.addEventListener ? mq.addEventListener('change', handler) : mq.addListener(handler);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', handler) : mq.removeListener(handler);
    };
  }, []);

  // Only attach <source> when wrapper scrolls into viewport
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el || inView) return;
    if (!('IntersectionObserver' in window)) {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            io.disconnect();
          }
        });
      },
      { rootMargin: '200px 0px', threshold: 0.01 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [inView]);

  // Auto-play when ready if autoPlay requested
  useEffect(() => {
    if (!autoPlay || !inView || !videoRef.current) return;
    const v = videoRef.current;
    const p = v.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  }, [autoPlay, inView]);

  const play = () => {
    const v = videoRef.current;
    if (!v) return;
    const p = v.play();
    if (p && typeof p.then === 'function') {
      p.then(() => setIsPlaying(true)).catch(() => {});
    } else {
      setIsPlaying(true);
    }
  };

  const pause = () => {
    const v = videoRef.current;
    if (!v) return;
    v.pause();
    try { v.currentTime = 0; } catch (_) {}
    setIsPlaying(false);
  };

  const handleMouseEnter = () => {
    if (hoverToPlay && canHover) play();
  };
  const handleMouseLeave = () => {
    if (hoverToPlay && canHover) pause();
  };
  const handleClick = () => {
    // On touch/no-hover: tap toggles play
    if (hoverToPlay && !canHover) {
      if (isPlaying) pause(); else play();
    }
  };

  return (
    <div
      ref={wrapperRef}
      className={`relative ${className}`}
      style={{ aspectRatio }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
    >
      {/* Poster always on top until video actually plays */}
      {poster && !isPlaying && (
        <img
          src={poster}
          alt=""
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover pointer-events-none z-10"
        />
      )}

      {/* Tap-to-play hint on touch devices */}
      {hoverToPlay && !canHover && !isPlaying && (
        <div className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none">
          <div className="w-12 h-12 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center">
            <svg className="w-6 h-6 text-white ml-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
            </svg>
          </div>
        </div>
      )}

      {/* Render <video> only when in viewport. preload="metadata" = ~tens of KB */}
      {inView && (
        <video
          ref={videoRef}
          className="w-full h-full object-cover"
          controls={controls}
          muted={muted}
          loop={loop}
          playsInline={playsInline}
          preload="metadata"
          poster={poster}
        >
          <source src={src} type="video/mp4" />
        </video>
      )}

      {/* Placeholder when no poster and not yet loaded */}
      {!poster && !inView && (
        <div className="absolute inset-0 w-full h-full bg-secondary/50 flex items-center justify-center pointer-events-none">
          <div className="animate-pulse">
            <svg className="w-12 h-12 text-muted-foreground" fill="currentColor" viewBox="0 0 20 20">
              <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
            </svg>
          </div>
        </div>
      )}
    </div>
  );
});

LazyVideo.displayName = 'LazyVideo';

export default LazyVideo;
